import { QoS } from 'mqtt';
import { Observable, Subscription } from 'rxjs';
import { MqttTransformationType } from '../models/mqtt-transformation-type';
import { ObservableSubscribe } from '../models/observable-subscribe';
import { TopicPayload } from '../models/topic-payload';
import { MqttService } from '../mqtt-service';

/**
 * Subscribes to an mqtt topic with automatic reconnection handling.
 * If client is disconnected for more than 30 minutes, the reconnect errors and has to resubscribe.
 * Ideally things might want to reload intelligently instead of just re-subbing (sync service uses pass through)
 *
 * @returns ObservableSubscribe<TopicPayload<T>>
 */
export function subscribeWithReconnect<T>(
    mqttService: MqttService,
    topic: string,
    qos: QoS = 1,
    transform: MqttTransformationType = MqttTransformationType.json,
    onSubscribe?: () => void,
) {
    if (!mqttService || mqttService.isClientTornDown) throw new Error('MQTT Service is not initialized or has been torn down');
    let base = mqttService.subscribe<T>(topic, qos, transform);
    let initialSubscribed = true;
    const returning = new Observable(obs => {
        let sub: Subscription;
        const resubscribe = () => {
            if (mqttService.isClientTornDown) {
                console.log(`Terminating reconnect subscription for topic ${topic}`);
                obs.complete();
                return;
            }

            if (!initialSubscribed) {
                console.log(`Resubbing to ${topic}`);
                base = mqttService.subscribe<T>(topic, qos, transform);
            } else initialSubscribed = false;
            sub = base.subscribe(
                { next: val => obs.next(val), error: () => resubscribe() },
            );
            onSubscribe?.();
        }
        resubscribe();
        return () => sub.unsubscribe();
    }) as ObservableSubscribe<TopicPayload<T>>;
    returning.subscribed = base.subscribed;
    return returning;
}
